import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ModalAtom from "../../../../components/atoms/modal/modal";
import TextFieldAtom from "../../../../components/atoms/text-field/text-field";

/**
 * AVA schema tier type identifiers - mirrors the `tierType` field in
 * projectInformation.itemNumberSchema.tiers (AVA Cloud API contract).
 */
const TIER_TYPE = {
    GROUP: "Group",
    POSITION: "Position",
    INDEX: "Index",
};

// Accent colours: Group → indigo, Subgroup → cyan, Position → emerald
const TIER_ACCENT = ["#4f46e5", "#0891b2", "#059669"];

/**
 * "Struktur des Leistungsverzeichnisses festlegen" - Cosuno-style schema editor.
 *
 * Props
 * ─────
 * show    {boolean}   – controls visibility
 * onHide  {Function}  – called on cancel / backdrop click
 * schema  {Object}    – current projectInformation.itemNumberSchema
 * onSave  {Function}  – called with the edited schema; caller must force-regenerate OZs
 */
const BoQSchemaModal = ({ show, onHide, schema, onSave }) => {
    const { t } = useTranslation();

    // Local editable copy - reset every time the modal opens.
    const [tiers, setTiers] = useState([]);

    useEffect(() => {
        if (show && schema?.tiers) {
            setTiers(schema.tiers.map((tier) => ({ ...tier })));
        }
    }, [show, schema]);

    const updateTier = (idx, field, rawValue) => {
        const parsed = parseInt(rawValue, 10);
        if (isNaN(parsed) || parsed < 1) return;
        setTiers((prev) =>
            prev.map((tier, i) => (i === idx ? { ...tier, [field]: parsed } : tier)),
        );
    };

    const handleSave = () => {
        onSave({ ...schema, tiers });
        onHide();
    };

    // The Index tier is fixed (Alphanumeric, always length 1) - not user-editable.
    const editableTiers = useMemo(
        () => tiers.map((tier, idx) => ({ tier, idx })).filter(({ tier }) => tier.tierType !== TIER_TYPE.INDEX),
        [tiers],
    );

    const tierLabel = (tier, idx) => {
        if (tier.tierType === TIER_TYPE.GROUP) {
            return idx === 0
                ? t("boq.schema-modal.level-group")
                : t("boq.schema-modal.level-subgroup");
        }
        if (tier.tierType === TIER_TYPE.POSITION) return t("boq.schema-modal.level-position");
        return tier.tierType;
    };

    // Live sample OZ - e.g. "01.01.0010"
    const ozPreview = useMemo(() => {
        const sep = schema?.separator ?? ".";
        const parts = editableTiers.map(({ tier }) => {
            const sample = tier.tierType === TIER_TYPE.POSITION ? tier.increment : 1;
            return String(sample).padStart(tier.length, "0");
        });
        return parts.join(sep);
    }, [editableTiers, schema]);

    const body = (
        <>
            <p className="text-muted mb-4" style={{ fontSize: "0.8125rem" }}>
                {t("boq.schema-modal.description")}
            </p>

            {/* Column headers */}
            <div className="d-flex align-items-center gap-3 mb-2 px-1">
                <div style={{ flex: 1 }} />
                <div style={{ width: 96, fontSize: "0.675rem", letterSpacing: "0.06em", textTransform: "uppercase", color: "#94a3b8", fontWeight: 600, textAlign: "center" }}>
                    {t("boq.schema-modal.col-digits")}
                </div>
                <div style={{ width: 96, fontSize: "0.675rem", letterSpacing: "0.06em", textTransform: "uppercase", color: "#94a3b8", fontWeight: 600, textAlign: "center" }}>
                    {t("boq.schema-modal.col-step")}
                </div>
            </div>

            {/* Tier cards */}
            <div className="d-flex flex-column gap-2 mb-3">
                {editableTiers.map(({ tier, idx }, cardIdx) => {
                    const accent = TIER_ACCENT[cardIdx] ?? "#94a3b8";
                    return (
                        <div
                            key={idx}
                            className="d-flex align-items-center gap-3 rounded-3 px-3"
                            style={{
                                minHeight: 54,
                                background: "#f8fafc",
                                border: "1px solid #e2e8f0",
                                borderLeft: `3px solid ${accent}`,
                            }}
                        >
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="fw-semibold" style={{ fontSize: "0.875rem" }}>
                                    {tierLabel(tier, idx)}
                                </div>
                                <div
                                    className="font-monospace"
                                    style={{ fontSize: "0.7rem", color: accent, letterSpacing: "0.12em" }}
                                >
                                    {"0".repeat(tier.length)}
                                </div>
                            </div>

                            {/* Digit length */}
                            <div style={{ width: 96 }}>
                                <TextFieldAtom
                                    type="number"
                                    min={1}
                                    max={tier.tierType === TIER_TYPE.POSITION ? 10 : 6}
                                    value={tier.length}
                                    onChange={(e) => updateTier(idx, "length", e.target.value)}
                                    size="sm"
                                    className="text-end"
                                />
                            </div>

                            {/* Step */}
                            <div style={{ width: 96 }}>
                                <TextFieldAtom
                                    type="number"
                                    min={1}
                                    max={100}
                                    value={tier.increment}
                                    onChange={(e) => updateTier(idx, "increment", e.target.value)}
                                    size="sm"
                                    className="text-end"
                                />
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Live OZ preview */}
            <div
                className="d-flex align-items-center gap-2 rounded-3 px-3 py-2 mb-3"
                style={{ background: "#f0f9ff", border: "1px solid #bae6fd" }}
            >
                <span style={{ fontSize: "0.75rem", color: "#64748b", whiteSpace: "nowrap" }}>
                    {t("boq.schema-modal.preview")}
                </span>
                <code style={{ fontSize: "0.9375rem", fontWeight: 600, color: "#0369a1", letterSpacing: "0.05em" }}>
                    {ozPreview}
                </code>
            </div>

            {/* Warning */}
            <div
                className="d-flex align-items-start gap-2 rounded-2 px-3 py-2"
                style={{ background: "#fffbeb", border: "1px solid #fde68a" }}
            >
                <span style={{ color: "#d97706", fontSize: "0.875rem", lineHeight: "1.5rem" }}>⚠</span>
                <p className="mb-0" style={{ fontSize: "0.8rem", color: "#92400e", lineHeight: 1.5 }}>
                    {t("boq.schema-modal.resequence-notice")}
                </p>
            </div>
        </>
    );

    return (
        <ModalAtom
            show={show}
            handleClose={onHide}
            handleSubmit={handleSave}
            title={t("boq.schema-modal.title")}
            body={body}
            saveText="boq.schema-modal.save"
            size="lg"
        />
    );
};

export default BoQSchemaModal;
